import { FC, createContext, useContext, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";

import { dispatchGetBandAction } from "Bands/state";
import {
  IBandSubscription,
  SubscriptionStatus,
  SubscriptionTypeWithTrial,
} from "Bands/types";
import { useBandContext } from ".";

interface BandSubscriptionContextState {
  currentSubscription?: IBandSubscription;
  isTrial: boolean;
  isActiveTrial: boolean;
  isActiveSubscription: boolean;
  fetchBandSubscriptions: () => void;
}

const initialState: BandSubscriptionContextState = {
  isTrial: false,
  isActiveTrial: false,
  isActiveSubscription: true,
  fetchBandSubscriptions: () => {},
};

const BandSubscriptionContext =
  createContext<BandSubscriptionContextState>(initialState);

export const BandSubscriptionProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const { bandId, band } = useBandContext();

  const currentSubscription = useMemo(
    () => band?.subscription,
    [band?.subscription]
  );

  const isTrial = useMemo(() => {
    return currentSubscription?.plan === SubscriptionTypeWithTrial.trial;
  }, [currentSubscription]);

  const isActiveTrial = useMemo(() => {
    return isTrial && currentSubscription?.status === SubscriptionStatus.active;
  }, [currentSubscription, isTrial]);

  const isActiveSubscription = useMemo(() => {
    return (
      !isTrial && currentSubscription?.status === SubscriptionStatus.active
    );
  }, [currentSubscription, isTrial]);

  const fetchBandSubscriptions = useCallback(() => {
    if (!bandId) return;

    // fetch band because subscription is part of band (band.subscription)
    dispatchGetBandAction(dispatch, bandId);
  }, [bandId, dispatch]);

  return (
    <BandSubscriptionContext.Provider
      value={{
        currentSubscription,
        isTrial,
        isActiveTrial,
        isActiveSubscription,
        fetchBandSubscriptions,
      }}
    >
      {children}
    </BandSubscriptionContext.Provider>
  );
};

export const useBandSubscriptionContext = () =>
  useContext(BandSubscriptionContext);
